import { toast } from "sonner";
/* eslint-disable jsx-a11y/anchor-is-valid */
import * as React from "react";
import {useEffect, useState} from "react";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import Fuse from "fuse.js";
import {FilterCombobox} from "../common/FilterCombobox";
import SortableTableColumnHeader from "../common/SortableTableColumnHeader";
import JobRequestStatusChip from "../Jobs/JobRequestStatusChip";
import AbortJobModal from "../Jobs/AbortJobModal";
import {compareBy, Order, stableSort} from "../../helpers/TableHelpers";
import JobsRepository from "../../repositories/JobsRepository";
import {JobRequestStatus, JobRequestStatusColors, JobRequestStatusIcons, JobRequestStatusLabels,} from "../../models/Jobs/JobRequestStatus";
import {DeleteFirmDocumentsFolderRequest, JobRequest} from "../../models/Jobs/JobRequest";
import ShortenedPath from "../common/ShortenedPath";
import ConfirmMigrationEntitiesDeletionModal, {CONFIRM_MIGRATION_ENTITIES_DELETION_CONFIGS} from "../common/ConfirmMigrationEntitiesDeletionModal";
import {Search as SearchIcon, Trash2, XCircle} from "lucide-react";
import {cn} from "@/lib/utils";
import {Table, TableHeader, TableBody, TableRow, TableHead, TableCell} from "@/components/ui/table";
import {
  drawerFiltersBoxClassName,
  drawerFilterItemClassName,
  filterSearchClassName,
  drawerTableWrapperClassName,
} from "../common/Table/TableStyles";
import {Button} from "@/components/ui/button";
import {Label} from "@/components/ui/label";
import {Input} from "@/components/ui/input";
import { QUERY_KEYS } from "../../constants/QueryKeys";
import { BulkDeletionSummary } from "../common/BulkDeletionSummary";

interface DeleteFirmDocumentsFoldersTableProps {
  organizationId: string;
  newFirmDocumentsFoldersForDeletion: DeleteFirmDocumentsFolderRequest[];
  setNewFirmDocumentsFoldersForDeletion: React.Dispatch<React.SetStateAction<DeleteFirmDocumentsFolderRequest[]>>;
}

const DeleteFirmDocumentsFoldersTable: React.FC<DeleteFirmDocumentsFoldersTableProps> = ({
  organizationId,
  newFirmDocumentsFoldersForDeletion,
  setNewFirmDocumentsFoldersForDeletion,
}) => {
  const queryClient = useQueryClient();
  const jobsRepository = new JobsRepository();
  const [order, setOrder] = React.useState<Order>("asc");
  const [sort, setSort] = React.useState<keyof JobRequest>("status");
  const [statusFilter, setStatusFilter] = React.useState<string | null>(null);
  const [searchForFolderPath, setSearchForFolderPath] = useState<string | null>("");
  const [abortJob, setAbortJob] = useState<JobRequest | null>(null);
  const [confirmDeletionModalOpen, setConfirmDeletionModalOpen] = useState<boolean>(false);
  const [firmDocumentsFoldersDeletionStatus, setFirmDocumentsFoldersDeletionStatus] = useState<DeleteFirmDocumentsFolderRequest[] | null>(null);

  const firmDocumentsFoldersQuery = useQuery({ queryKey: [QUERY_KEYS.FIRM_DOCUMENTS_FOLDER_DELETION_STATUS, organizationId], queryFn:
    async () => {
      return await jobsRepository.getFirmDocumentsFoldersDeletionStatus(organizationId);
    }, staleTime: 5 * 60 * 1000,
      refetchInterval: 5 * 60 * 1000 + 1,
      refetchIntervalInBackground: true,
      refetchOnMount: true, });

  useEffect(() => {
    if (firmDocumentsFoldersQuery.data) {
      setFirmDocumentsFoldersDeletionStatus(firmDocumentsFoldersQuery.data);
    }
  }, [firmDocumentsFoldersQuery]);

  const handleSearch = (newFolderPath: string) => {
    setSearchForFolderPath(newFolderPath ?? null);
  };

  const changeSort = (newSort: keyof JobRequest) => { setSort(newSort); };
  const changeOrder = (newOrder: Order) => { setOrder(newOrder); };

  const { isError, isFetching } = firmDocumentsFoldersQuery;
  const loading = isFetching || isError;

  const renderFilters = () => (
    <React.Fragment>
      <div className={drawerFilterItemClassName}>
        <Label className="text-sm">Status</Label>
        <FilterCombobox
          value={statusFilter}
          onChange={(newValue) => { setStatusFilter(newValue); }}
          placeholder="Status"
          options={Object.values(JobRequestStatus).map((status) => {
            const deleteRequestStatus = status as JobRequestStatus;
            return {
              value: deleteRequestStatus,
              label: JobRequestStatusLabels[deleteRequestStatus],
              color: JobRequestStatusColors[deleteRequestStatus],
              icon: JobRequestStatusIcons[deleteRequestStatus],
            };
          })}
        />
      </div>
    </React.Fragment>
  );

  const filteredJobs = (firmDocumentsFoldersDeletionStatus || []).filter((job) => {
    return statusFilter == null || job.status === statusFilter;
  });

  const searchOptions = {
    keys: ["folderPath"],
    includeScore: true,
    threshold: 0.1,
    caseSensitive: false,
  };

  const fuse = new Fuse(filteredJobs, searchOptions);
  const searchResults = searchForFolderPath
    ? fuse.search(searchForFolderPath).map((result) => result.item)
    : filteredJobs;

  const sortedJobs = sort
    ? stableSort<DeleteFirmDocumentsFolderRequest>(searchResults, compareBy(order, sort))
    : filteredJobs;

  const allRows = [...newFirmDocumentsFoldersForDeletion, ...sortedJobs];
  const newIds = new Set(newFirmDocumentsFoldersForDeletion.map((f) => f.migrationEntityId));

  return (
    <div className="flex flex-col w-full flex-1 min-h-0">
      <div className={cn(drawerFiltersBoxClassName, "pt-1")}>
        <div className={filterSearchClassName}>
          <Label className="text-sm">Search folders</Label>
          <div className="relative">
            <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input className="pl-8" placeholder="Folder path" onChange={(event) => handleSearch(event.target.value)} />
          </div>
        </div>
        {renderFilters()}
      </div>
      <Button disabled={!(newFirmDocumentsFoldersForDeletion.length > 0)} onClick={() => setConfirmDeletionModalOpen(true)} className="mb-1">
        Delete Firm Documents Folders
      </Button>
      <div className={cn(drawerTableWrapperClassName, loading && "opacity-50 pointer-events-none")}>
        <Table containerClassName="flex-1 min-h-0">
          <TableHeader>
            <TableRow>
              <TableHead className="w-0 text-center" />
              <TableHead><SortableTableColumnHeader label={"Folder Path"} sortProperty={"folderPath"} getSort={() => sort} setSort={(newSort) => changeSort(newSort as keyof JobRequest)} getOrder={() => order} setOrder={(newOrder) => changeOrder(newOrder)} /></TableHead>
              <TableHead><SortableTableColumnHeader label={"Status"} sortProperty={"status"} getSort={() => sort} setSort={(newSort) => changeSort(newSort as keyof JobRequest)} getOrder={() => order} setOrder={(newOrder) => changeOrder(newOrder)} /></TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {allRows.map((deleteFirmDocumentsFolderJob: DeleteFirmDocumentsFolderRequest) => {
              const canEdit =
                deleteFirmDocumentsFolderJob.status === JobRequestStatus.Pending ||
                deleteFirmDocumentsFolderJob.status === JobRequestStatus.New ||
                deleteFirmDocumentsFolderJob.status === JobRequestStatus.Completed;

              return (
                <TableRow key={deleteFirmDocumentsFolderJob.id ?? deleteFirmDocumentsFolderJob.migrationEntityId} className={newIds.has(deleteFirmDocumentsFolderJob.migrationEntityId) ? "bg-primary/10" : ""}>
                  <TableCell className="text-center" />
                  <TableCell>
                    <span className="text-sm">
                      <ShortenedPath folderPath={deleteFirmDocumentsFolderJob.folderPath} maxLength={20} />
                      {deleteFirmDocumentsFolderJob.bulk && <BulkDeletionSummary bulk={deleteFirmDocumentsFolderJob.bulk} />}
                    </span>
                  </TableCell>
                  <TableCell><JobRequestStatusChip status={deleteFirmDocumentsFolderJob.status} /></TableCell>
                  {canEdit ? (
                    <TableCell>
                      <Button variant="ghost" size="icon-sm" className="text-destructive" aria-label="Cancel"
                        onClick={() => {
                          deleteFirmDocumentsFolderJob.id
                            ? setAbortJob(deleteFirmDocumentsFolderJob)
                            : setNewFirmDocumentsFoldersForDeletion((prevRows) =>
                                prevRows.filter((entity) =>
                                    entity.migrationEntityId !== deleteFirmDocumentsFolderJob.migrationEntityId
                              ));
                        }}>
                        {deleteFirmDocumentsFolderJob.status === JobRequestStatus.Completed
                          ? (<Trash2 className="size-4" />)
                          : (<XCircle className="size-4" />)}
                        
                      </Button>
                    </TableCell>
                  ) : (
                    <TableCell />
                  )}
                </TableRow>
              );
            })}
              {allRows.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={99} className="h-24 text-center text-muted-foreground">
                    No pending deletions.
                  </TableCell>
                </TableRow>
              )}
          </TableBody>
        </Table>
      </div>
      {abortJob ? (
        <AbortJobModal open={abortJob !== null} jobRequest={abortJob} onClose={() => { setAbortJob(null); }}
          onConfirm={async () => {
            await jobsRepository.deleteJob(abortJob.id);
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.JOBS] });
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FIRM_DOCUMENTS_FOLDER_DELETION_STATUS, organizationId] });
            if (abortJob.status !== JobRequestStatus.Completed) {
              queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FIRM_DOCUMENTS_FOLDERS, organizationId] });
            }
            setAbortJob(null);
          }}
        />
      ) : (<></>)}

      {confirmDeletionModalOpen ? (
        <ConfirmMigrationEntitiesDeletionModal open={confirmDeletionModalOpen} entityKeys={newFirmDocumentsFoldersForDeletion.map((f) => f.folderPath)} confirmMigrationDeletionConfig={CONFIRM_MIGRATION_ENTITIES_DELETION_CONFIGS.FIRM_DOCUMENTS_FOLDERS} onClose={() => { setConfirmDeletionModalOpen(false); }}
          onConfirm={async () => {
            await jobsRepository.startFirmDocumentsFoldersDeletion(organizationId, newFirmDocumentsFoldersForDeletion.map((f) => f.migrationEntityId));
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.JOBS] });
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FIRM_DOCUMENTS_FOLDER_DELETION_STATUS, organizationId] });
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FIRM_DOCUMENTS_FOLDERS, organizationId] });
            setNewFirmDocumentsFoldersForDeletion([]);
            setConfirmDeletionModalOpen(false);
            toast.success("Deletion started");
          }}
        />
      ) : (<></>)}
    </div>
  );
};

export default DeleteFirmDocumentsFoldersTable;
